/**
 * Detailed Xero setup guide — same shape as StripeSetupGuide. Rendered in
 * a Dialog with a print-friendly layout so the operator can save it as
 * PDF via the browser's built-in "Print → Save as PDF" flow.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BookOpen, Printer, ExternalLink } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export const XeroSetupGuide = () => {
  const doPrint = () => {
    document.body.classList.add("print-only-guide");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("print-only-guide"), 400);
    }, 40);
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="xero-setup-guide-btn">
          <BookOpen className="h-4 w-4 mr-1.5" /> Setup Guide
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto print-guide-content">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-2xl">Xero — Step-by-step Setup Guide</DialogTitle>
            <Button variant="outline" size="sm" onClick={doPrint} data-testid="xero-guide-print-btn" className="no-print shrink-0">
              <Printer className="h-4 w-4 mr-1.5" /> Print / Save as PDF
            </Button>
          </div>
        </DialogHeader>

        <div className="prose prose-sm max-w-none space-y-5 text-[13px] leading-relaxed">
          <section>
            <p className="text-neutral-700">
              This guide walks you through creating a Xero developer app, connecting it to your
              Xero organisation, and pasting the OAuth credentials into the laundry admin console.
              Set aside about <strong>10 minutes</strong>. You will need: an existing Xero account
              (any plan will do) and admin access to that Xero organisation.
            </p>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part A — Register a &quot;Web app&quot; in the Xero Developer portal</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Open <a className="text-brand-navy underline" href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer">https://developer.xero.com/app/manage <ExternalLink className="inline h-3 w-3"/></a> in a new tab.</li>
              <li>If it asks you to sign in, use the same Xero login you use for the Xero organisation you want to connect.</li>
              <li>You&apos;ll land on the <span className="font-mono">My Apps</span> page. Click the blue <span className="font-semibold">New app</span> button (top right).</li>
              <li>Fill the form:
                <ul className="list-disc pl-6 mt-1 space-y-0.5">
                  <li><strong>App name</strong>: something like <span className="font-mono">Laundry Admin</span>.</li>
                  <li><strong>Integration type</strong>: select <strong>Web app</strong>.</li>
                  <li><strong>Company or application URL</strong>: your business website (or the admin URL below).</li>
                  <li><strong>OAuth 2.0 redirect URI</strong>: paste EXACTLY
                    <div className="my-2 p-2 rounded bg-neutral-100 border font-mono text-xs break-all">{API_BASE}/api/xero/callback</div>
                    (Copy from the Settings page — it must match character-for-character or Xero will reject the connection.)
                  </li>
                </ul>
              </li>
              <li>Tick the terms box, click <span className="font-semibold">Create app</span>.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part B — Copy the Client ID and generate a Client Secret</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>You&apos;ll be dropped on the app&apos;s <span className="font-mono">Configuration</span> page. Note the <strong>Client ID</strong> at the top — copy it (32 hex characters).</li>
              <li>Scroll down to <span className="font-mono">Client secrets</span> and click <span className="font-semibold">Generate a secret</span>.</li>
              <li>Xero will show the secret <strong>once</strong> in a green banner. Click the copy icon immediately — you cannot retrieve it later, only regenerate.
                <br/><span className="text-red-700 font-semibold">⚠ Never share this or paste it into a chat/email. If you lose it, generate a new one and paste the new one into the admin console.</span></li>
              <li>Leave the Xero tab open — you&apos;ll re-authorise the app once the keys are saved.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part C — Paste keys into the laundry admin console</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Return to this Settings page.</li>
              <li>Paste the <strong>Client ID</strong> into the <span className="font-semibold">Client ID</span> field.</li>
              <li>Paste the <strong>Client Secret</strong> into the <span className="font-semibold">Client Secret</span> field.</li>
              <li>Leave <span className="font-semibold">Sales Account Code</span> as <span className="font-mono">200</span> (default revenue account) unless your accountant told you otherwise. Same for <span className="font-semibold">Fees Account Code</span> (<span className="font-mono">508</span> = default expenses).</li>
              <li>Click <span className="font-semibold">Save Xero Settings</span>.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part D — Authorise the connection to your Xero organisation</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Back on this Settings page, click the blue <span className="font-semibold">Connect to Xero</span> button. A popup will open.</li>
              <li>Sign in to Xero if it asks. Then choose the organisation you want to connect (if you have multiple).</li>
              <li>Xero shows a permissions screen: <span className="font-mono">accounting.transactions</span>, <span className="font-mono">accounting.contacts</span>, <span className="font-mono">accounting.settings</span>. Click <span className="font-semibold">Allow access</span>.</li>
              <li>The popup will close automatically and return to the admin. Click <span className="font-semibold">Test Connection</span> — you should see <span className="text-emerald-700 font-semibold">green &quot;Connected to {'{Your Company}'}&quot;</span>.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-bold text-base mt-4">Part E — Sanity check</h3>
            <ol className="list-decimal pl-6 space-y-1.5 mt-1">
              <li>Head into <span className="font-mono">Orders → any dispatched order → Create invoice</span>. Wait for the toast to say &quot;invoice pushed to Xero&quot;.</li>
              <li>Open Xero → <span className="font-mono">Business → Invoices → Awaiting Payment</span>. Your invoice should appear (usually within 5 seconds).</li>
              <li>If it doesn&apos;t appear, click <span className="font-semibold">Refresh</span> on the Settings page — the status card will show the exact error message from Xero.</li>
            </ol>
          </section>

          <section className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200">
            <p className="font-semibold text-amber-900">If something goes wrong</p>
            <ul className="list-disc pl-6 mt-1 space-y-1 text-amber-900 text-[12px]">
              <li><strong>&quot;Redirect URI mismatch&quot; on Connect</strong> — the URI in Part A step 4 doesn&apos;t match this app&apos;s callback. Copy the URI from the Settings page and paste it into your Xero app&apos;s <span className="font-mono">OAuth 2.0 redirect URIs</span> box, then Save.</li>
              <li><strong>&quot;invalid_client&quot;</strong> — Client Secret is wrong or expired. Go back to Part B step 2, generate a new one, paste it here and re-Save.</li>
              <li><strong>Invoice creation fails with &quot;Account code not found&quot;</strong> — the account codes in the Sales/Fees fields don&apos;t exist in your Chart of Accounts. Ask your accountant for the right codes, or open Xero → <span className="font-mono">Accounting → Chart of Accounts</span> and pick codes that already exist.</li>
              <li><strong>Connection expired</strong> — Xero refresh tokens rotate. The app auto-refreshes them, but if you don&apos;t use Xero for 60 days it may still expire. Click <span className="font-semibold">Connect to Xero</span> again to reauthorise.</li>
            </ul>
          </section>

          <p className="text-[11px] text-muted-foreground pt-2 border-t">
            Generated by the Laundry admin console. Credentials entered here are stored in the
            shared database and survive every code deploy — you do not need to redo this after we
            push updates. Rotate the Client Secret anytime in Xero → App → Client secrets, then
            paste the new value here and Save.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
